import { useCallback, useState } from 'react'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'

import type { AgentInsightService } from 'features/trend-detection/features/insight-agent/service'
import {
  ResearchAgentsAction,
  trackResearchAgents,
} from 'features/trend-detection/mixpanel'

export interface UseGeneralAgentCreateSubmitParams {
  projectId: string | null
  service: AgentInsightService
  name: string
  prompt: string
}

export interface UseGeneralAgentCreateSubmitResult {
  submit: () => Promise<boolean>
  isSubmitting: boolean
}

/** Reports (general) Agent's create-and-run. Backend wiring is a no-op
 * placeholder still, mirrored from the deleted `handleCreateGeneralAgent` —
 * see `api/features/features/reports_agent/DESIGN.md`. */
export function useGeneralAgentCreateSubmit({
  projectId,
  service,
  name,
  prompt,
}: UseGeneralAgentCreateSubmitParams): UseGeneralAgentCreateSubmitResult {
  const queryClient = useQueryClient()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const submit = useCallback(async (): Promise<boolean> => {
    if (!projectId || isSubmitting) return false
    setIsSubmitting(true)
    trackResearchAgents(ResearchAgentsAction.agent_create, {
      agent_type: 'general',
      agent_name: name,
    })
    try {
      const agent = await service.createAgent(projectId, name, prompt)
      // Creating without running leaves an agent with nothing to show, so
      // the run is dispatched here. It is a BACKGROUND run — this resolves
      // as soon as the run row exists, not when insights are ready, so the
      // toast has to say where they'll appear rather than implying they
      // already have.
      await service.runAgent(projectId, agent.uuid)
      queryClient.invalidateQueries(['insight-agents', projectId])
      toast.success(
        `"${agent.name}" is running. Its insights will appear in the ` +
          'Agent insights tab of the insights feed when it finishes.',
      )
      return true
    } catch {
      toast.error('Could not create the insight agent. Please try again.')
      return false
    } finally {
      setIsSubmitting(false)
    }
  }, [projectId, isSubmitting, service, name, prompt, queryClient])

  return { submit, isSubmitting }
}
